import { axios } from '/plugins/axios.client';
import { errorFromResponse } from '/src/common/general-utils';
import { i18n } from '/plugins/i18n.client';
const errorHandler = errorFromResponse(i18n.global.t.bind(i18n));
import { toast } from '/plugins/toaster.client.js';

/**
 * Fetch objects list.
 * @param {String} [searchString=''] filter objects list based on it.
 * @param {String} [noOfRecords=20] limit the number of records at a time.
 * @param {String} [selectedUUIDs] comma separated list of UUIDs
 * @param {String} [pageNo=1] Page offset
 * @param {String} [orderByField='UUID'] What to order by
 * @param {String} [orderByDir] Direction of ordering ASC/DESC
 * @returns {array}
 */
export const getObjectsList = async ({
  searchString = '',
  noOfRecords = 20,
  pageNo = 1,
  orderByField = 'UUID',
  orderByDir = null,
  selectedUUIDs = '',
  fetchAppPackage = undefined,
  isDetailedAuditTrail
} = {}) => {
  try {
    let { data } = await axios.$get(`/api/object`, {
      params: {
        listType: 'active',
        searchString,
        pageNo,
        noOfRecords,
        'orderBy[field]': orderByField,
        'orderBy[dir]': orderByDir,
        selectedObjectUUIDs: selectedUUIDs,
        fetchAppPackage,
        isDetailedAuditTrail
      }
    });
    return data;
  } catch (e) {
    toast.error(
      errorHandler(
        'getting object List',
        e,
        i18n.global.t('admin.target_state_selector.list_fetch_error', { feature: 'object' })
      )
    );
  }
  return null;
};

/**
 * Fetch dashboards list.
 * @param {String} [searchString=''] filter dashboards list based on it.
 * @param {String} [noOfRecords=20] limit the number of records at a time.
 * @param {String} [selectedUUID] ID of already selected dashboard
 * @param {String} [pageNo=1] Page offset
 * @param {String} [orderByField='UUID'] What to order by
 * @param {String} [orderByDir] Direction of ordering ASC/DESC
 * @returns {array} List of dashboards.
 */
export const getPagesList = async ({
  searchString = '',
  noOfRecords = 20,
  pageNo = 1,
  orderByField = 'UUID',
  orderByDir = null,
  selectedUUID,
  headers = {}
} = {}) => {
  try {
    let { data } = await axios.$get(`/api/pages`, {
      params: {
        searchString,
        pageNo,
        noOfRecords,
        'orderBy[field]': orderByField,
        'orderBy[dir]': orderByDir,
        selectedDashboard: selectedUUID
      },
      headers
    });
    return data;
  } catch (e) {
    toast.error(
      errorHandler(
        'getting page List',
        e,
        i18n.global.t('admin.target_state_selector.list_fetch_error', { feature: 'Dashboards' })
      )
    );
  }
  return null;
};

/**
 * @method getAllWizardsList
 * @description
 *
 * Method is used to fetch the list of form wizards.
 * @returns {array} List of wizards.
 */
export const getAllWizardsList = async (requireExtWizard, excludeExtWizard) => {
  try {
    return await axios.$get('/api/wizard', {
      params: {
        requireExtWizard,
        excludeExtWizard
      }
    });
  } catch (e) {
    toast.error(
      errorHandler(
        'getting wizards List',
        e,
        i18n.global.t('admin.target_state_selector.list_fetch_error', { feature: 'Form Wizards' })
      )
    );
  }
  return null;
};

/**
 * Fetch object templates.
 * @returns {array} List of object templates.
 */
export const getObjectUIsList = async (ObjectUUID) => {
  if (!ObjectUUID) {
    toast.error(i18n.global.t('admin.target_state_selector.required_field', { field: 'object UUID' }));
    return null;
  }

  try {
    return await axios.$get(`/api/objectUI?ObjectUUID=${ObjectUUID}`);
  } catch (e) {
    toast.error(
      errorHandler(
        'getting object UIs List',
        e,
        i18n.global.t('admin.target_state_selector.list_fetch_error', { feature: 'Object UIs' })
      )
    );
  }
  return null;
};

/**
 * Fetch business-rules for selection in dropdown.
 * @param {String} searchString param object.
 * @param {Array} selectedUUIDs array of BR-uuids
 * @returns {Array} List of business-rules.
 */
export const getBusinessRulesList = async ({ searchString, selectedUUIDs = [], fields = ['UUID', 'Name'] } = {}) => {
  try {
    return await axios.$get(`/api/v2/business-rules/withfields`, {
      params: {
        fields: JSON.stringify(fields),
        isArchived: false,
        /* If undefined or null then set to empty string as this will restrict results to 10 rows at a time */
        searchBusinessRule: searchString || '',
        businessRules: JSON.stringify(selectedUUIDs)
      }
    });
  } catch (e) {
    toast.error(
      errorHandler(
        'getting business rules list',
        e,
        i18n.global.t('admin.target_state_selector.list_fetch_error', { feature: 'Business rules' })
      )
    );
  }
  return null;
};

export const getTaskCockpitAccess = async (userId, groupIDs) => {
  try {
    return await axios.$post('/api/users/task-cockpit-access', { userId, groupIDs });
  } catch (error) {
    return false;
  }
};

export const getApplicationsList = async (hiddenApps = null, params = {}) => {
  try {
    return await axios.$get('/api/appManager/getApps', {
      params: {
        ...params,
        hiddenApps: hiddenApps
      }
    });
  } catch (error) {
    toast.error(errorHandler('admin.applications.actions.fetch_applications', error));
    return [];
  }
};

export const getKpiList = async (businessRuleUUID) => {
  try {
    return await axios.$get(`/api/kpis/${businessRuleUUID}/queryByRuleRef`);
  } catch (error) {
    toast.error(
      errorHandler(
        i18n.global.t('admin.dynamic_labels.fetch_action', {
          feature: i18n.global.t('common.businessrules.kpis.header')
        }),
        error
      )
    );
    return [];
  }
};

export const getAppDetails = async (uuid) => {
  try {
    const app = await axios.$get(`/api/appManager/getApps/${uuid}`);
    return app[0];
  } catch (e) {
    return null;
  }
};
